import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useQueryCallbacks } from 'core/hooks/data/useQueryCallbacks'
import APIService from 'api/APIService'
import { ID, JobStatus, Team, UpdateTeamReportsParams } from 'types/entity'
import { useSettings } from 'core/hooks/settings/useSettings'
import useNotifications from 'core/hooks/ui/useNotifications'
import useSessionStorage from 'core/hooks/utils/useSessionStorage'
import useCurrentTeam from 'core/hooks/team/useCurrentTeam'
import useRolesAndPermissions from 'core/hooks/user/useRolesAndPermissions'
import { useEffect } from 'react'
import { PayPeriodsUrl } from 'constants/RouteUrls'
import useNavigation from 'core/hooks/navigation/useNavigation'

interface PayPeriodActionProps {
  onSuccess?: VoidFunction
  lastAllowedAction: string
}

interface PayPeriodLocalStorage {
  payPeriodId: ID
  action: string
  jobId: ID
}

const useReportsAction = (
  { onSuccess, lastAllowedAction }: PayPeriodActionProps = {
    lastAllowedAction: ''
  }
) => {
  const { pathname } = useNavigation()
  const [reportsJobData, setReportsJobData] =
    useSessionStorage<PayPeriodLocalStorage>(
      'reports_batch_update_action_job',
      {
        payPeriodId: '',
        action: '',
        jobId: ''
      }
    )
  const queryClient = useQueryClient()
  const {
    data: { pollingJobs },
    set
  } = useSettings()
  const notification = useNotifications()
  const {
    roles: { isSuperAdmin }
  } = useRolesAndPermissions()
  const { data: team } = useCurrentTeam()
  const teamId = isSuperAdmin ? team?.root_team?.token_id : team?.token_id

  const jobID = pollingJobs.reportsActionJobId || reportsJobData.jobId

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (pollingJobs.reportsActionJobId === '' && reportsJobData.jobId) {
        set('pollingJobs', {
          ...pollingJobs,
          reportsActionJobId: reportsJobData.jobId
        })
      }
    }, 300)
    return () => clearTimeout(timeout)
  }, [pollingJobs, reportsJobData.jobId, set])

  useQueryCallbacks({
    queryKey: ['reports-batch-update-action-polling', jobID],
    queryFn: () =>
      APIService.misc.getJobStatus({
        jobId: jobID
      }),
    enabled: !!jobID,
    refetchInterval: 2000,
    onSuccess: (response: JobStatus) => {
      if (response.status === 'finished' && !response.data?.errors) {
        notification.dismiss('reportsActionJobId')
        setReportsJobData({
          ...reportsJobData,
          jobId: ''
        })
        set('pollingJobs', {
          ...pollingJobs,
          reportsActionJobId: ''
        })
        queryClient.invalidateQueries({
          queryKey: reportsJobData.payPeriodId
            ? ['pay-periods', reportsJobData.payPeriodId]
            : ['pay-periods']
        })
        queryClient.invalidateQueries({ queryKey: ['reports'] })
        notification.success({
          title: response.data.message as string,
          id: String(jobID)
        })
      }

      if (
        response.status === 'failed' ||
        (response.status === 'finished' && response.data?.errors)
      ) {
        notification.dismiss('reportsActionJobId')
        notification.error({
          title: 'There were some errors updating your pay period',
          subtitle: (
            <div>
              {typeof response.data?.message === 'string' &&
                response.data.message?.split(';').map((error) => {
                  return <span key={error}>{error.replace(';', '')}</span>
                })}
            </div>
          ),
          id: String(jobID)
        })
        setReportsJobData({
          ...reportsJobData,
          jobId: ''
        })
        set('pollingJobs', {
          ...pollingJobs,
          reportsActionJobId: ''
        })
      }

      if (response.status === 'finished') {
        queryClient.invalidateQueries({
          queryKey: reportsJobData.payPeriodId
            ? ['pay-periods', reportsJobData.payPeriodId]
            : ['pay-periods']
        })
        queryClient.invalidateQueries({ queryKey: ['reports'] })
        setReportsJobData({
          payPeriodId: '',
          action: '',
          jobId: ''
        })
      }
    },
    onError: () => {
      notification.dismiss('reportsActionJobId')
      notification.error({
        title: 'Could not update pay period at this time',
        id: String(jobID)
      })
      setReportsJobData({
        ...reportsJobData,
        jobId: ''
      })
      set('pollingJobs', {
        ...pollingJobs,
        reportsActionJobId: ''
      })
    }
  })

  const updateQuery = useMutation({
    mutationKey: ['update-team-reports', teamId],
    mutationFn: ({
      teamId,
      params
    }: {
      teamId: Team['token_id']
      params: UpdateTeamReportsParams
    }) => {
      if (params.filter_params.pay_period_token_id) {
        setReportsJobData({
          ...reportsJobData,
          payPeriodId: params.filter_params.pay_period_token_id,
          action: lastAllowedAction
        })
      }
      return APIService.reports.updateTeamReports({
        teamId: teamId as string,
        params
      })
    },
    onSuccess: (data) => {
      if (pathname.startsWith(PayPeriodsUrl)) {
        notification.loading({
          title:
            lastAllowedAction === 'Approve'
              ? 'Approving reports...'
              : 'Updating reports...',
          subtitle: `This may take a few minutes. You can close this pop up and we'll keep working in the background. Pay Period actions will be unavailable until this process is complete.`,
          duration: Infinity,
          id: 'reportsActionJobId'
        })

        setReportsJobData({
          ...reportsJobData,
          jobId: data.token_id
        })
        setTimeout(() => {
          set('pollingJobs', {
            ...pollingJobs,
            reportsActionJobId: data.token_id
          })
        }, 0)
      } else {
        notification.success({
          title: 'Successfully performed action',
          duration: 5000
        })
      }
      onSuccess?.()
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      queryClient.invalidateQueries({ queryKey: ['pay-periods'] })
    },
    onError: () => {
      notification.error(
        `Could not ${lastAllowedAction}. Please try again later`
      )
    }
  })

  return updateQuery
}

export default useReportsAction
